import { SdbMemberTypes } from "@custom-types/member-types";
import { AutoCompleteOption, neutral, TeamIcon } from "@flat-ui";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { nounPluralize } from "@utils/data-display";
import {
  getPendingUserIcon,
  getPrettyName,
  isMemberActive,
} from "@utils/user-utils";

type CreateOptionBaseProps = {
  /**
   * True when the option should be disabled in dropdown
   * @default false
   */
  isDisabled?: boolean;

  /** Message besides the disabled option explaining the reasoning */
  disabledMessage?: string;

  /**
   * True when the option should be already preselected on initialization.
   * @default false
   */
  isPreselected?: boolean;
};

type CreateMemberOptionsProps = CreateOptionBaseProps & {
  /** Member data to create member option for AutoComplete*/
  member: SdbMemberTypes;
};

/** Create autocomplete options based on SdbMemberTypes */
export function createMemberOption({
  member,
  isDisabled = false,
  disabledMessage = "",
  isPreselected = false,
}: CreateMemberOptionsProps): AutoCompleteOption {
  return {
    isDisabled,
    disabledMessage,
    isPreselected,
    title: getPrettyName(member),
    subtitle: isMemberActive(member) ? member.email : "",
    value: member.identity,
    avatarImage: member.thumbnailUrl ?? "",
    avatarIcon: getPendingUserIcon(member),
  };
}

type CreateTeamOptionsProps = CreateOptionBaseProps & {
  /** Team data to create team option for AutoComplete */
  team: FaroCoreApiTypes.Team;
};

/** Create autocomplete options based on ITeam */
export function createTeamOption({
  team,
  isDisabled = false,
  disabledMessage = "",
  isPreselected = false,
}: CreateTeamOptionsProps): AutoCompleteOption {
  return {
    isDisabled,
    disabledMessage,
    isPreselected,
    title: team.name,
    subtitle: `${nounPluralize({
      counter: team.memberCount,
      word: "member",
    })}`,
    value: team.id,
    avatarIcon: <TeamIcon />,
    avatarStyle: { bgcolor: neutral[700] },
  };
}
